import Ajv, { type JSONSchemaType, type ValidateFunction } from "ajv";
import { useCallback, useMemo, useReducer, useState } from "react";
import type { BBJLocalStorage } from "./storages";

export type TypeGuard<T> = (value: unknown) => value is T;

export function createSchemaTypeGuard<T>(schema: JSONSchemaType<T>): TypeGuard<T>;
export function createSchemaTypeGuard<T>(validate: ValidateFunction<unknown>): TypeGuard<T>;
export function createSchemaTypeGuard<T>(input: JSONSchemaType<T> | ValidateFunction<unknown>): TypeGuard<T> {
  const validate: ValidateFunction<unknown> =
    typeof input === "function"
      ? (input as ValidateFunction<unknown>)
      : new Ajv({ allErrors: true, strict: false }).compile(input as JSONSchemaType<T>);
  return (value: unknown): value is T => Boolean(validate(value));
}

const getContainerObject = (storage: Storage, name: BBJLocalStorage): Record<string, unknown> => {
  const raw = storage.getItem(name);
  if (!raw) {
    return {};
  }
  try {
    const parsed = JSON.parse(raw);
    if (typeof parsed === "object" && parsed !== null) {
      return parsed;
    }
    return {};
  } catch {
    return {};
  }
};

const writeContainerObject = (storage: Storage, name: BBJLocalStorage, object: Record<string, unknown>) => {
  storage.setItem(name, JSON.stringify(object));
};

export const getStorageItem = <T>(
  storage: Storage,
  name: BBJLocalStorage,
  key: string,
  defaultValue: T,
  typeguard: TypeGuard<T>
): T => {
  const container = getContainerObject(storage, name);
  if (key in container) {
    const value = container[key];
    if (typeguard(value)) {
      return value;
    }
  }
  return defaultValue;
};

export const setStorageItem = <T>(storage: Storage, name: BBJLocalStorage, key: string, value: T) => {
  const container = getContainerObject(storage, name);
  writeContainerObject(storage, name, { ...container, [key]: value });
};

export const useStorage = <T>(
  name: BBJLocalStorage,
  key: string,
  defaultValue: T,
  typeguard: TypeGuard<T>,
  storage: Storage = localStorage
): [T, (value: T) => void] => {
  const [state, setState] = useState<T>(() => getStorageItem(storage, name, key, defaultValue, typeguard));

  const setValue = useCallback(
    (value: T) => {
      try {
        setStorageItem(storage, name, key, value);
        setState(value);
        window.dispatchEvent(new Event("storage"));
      } catch (error) {
        if (error instanceof DOMException && error.name === "QuotaExceededError") {
          storage.removeItem(name);
          setState(defaultValue);
        } else {
          console.error(error);
        }
        throw new Error("Erreur avec l'enregistrement local, veuillez contacter un administrateur.");
      }
    },
    [storage, name, key, defaultValue]
  );

  return [state, setValue];
};

export const useReducerStorage = <State, Action>(
  name: BBJLocalStorage,
  key: string,
  reducer: (state: State, action: Action) => State,
  initialState: State,
  typeguard: TypeGuard<State>,
  storage: Storage = localStorage
): [State, (action: Action) => void] => {
  const initial = useMemo(() => {
    return getStorageItem(storage, name, key, initialState, typeguard);
  }, [storage, name, key, initialState, typeguard]);

  const [state, dispatchBase] = useReducer(reducer, initial);

  const dispatch = useCallback(
    (action: Action) => {
      const next = reducer(state, action);
      try {
        setStorageItem(storage, name, key, next);
      } catch (error) {
        console.error(error);
      }
      dispatchBase(action);
    },
    [state, reducer, storage, name, key]
  );

  return [state, dispatch];
};
